import { useState } from "react";
import { useLocation } from "wouter";
import {
  Box,
  Flex,
  Icon,
  VStack,
  HStack,
  Button,
  Text,
  Heading,
  Spinner,
  IconButton,
} from "@chakra-ui/react";
import { MdArrowBack as BackIcon, MdStar as StarIcon } from "react-icons/md";
import { useTagGroups } from '@/lib/recipeService';
import { FILTER_EXCLUDED_TAG_GROUPS } from "@/constants/tags";
import { ROUTES } from "@/types";

const RATING_CHIPS: { label: string; value: number }[] = [
  { label: "2+", value: 2 },
  { label: "3+", value: 3 },
  { label: "4+", value: 4 },
  { label: "5", value: 5 },
];

export default function FiltersPage() {
  const [, setLocation] = useLocation();

  const initialTags = JSON.parse(
    sessionStorage.getItem("allSelectedTags") || "[]"
  ) as string[];

  if (!sessionStorage.getItem("originalSelectedTags")) {
    sessionStorage.setItem("originalSelectedTags", JSON.stringify(initialTags));
  }

  const initialMinRating = (() => {
    const raw = sessionStorage.getItem("activeMinRating");
    return raw !== null ? Number(raw) : null;
  })();

  if (!sessionStorage.getItem("originalMinRating")) {
    sessionStorage.setItem(
      "originalMinRating",
      initialMinRating !== null ? String(initialMinRating) : ""
    );
  }

  const [selectedTags, setSelectedTags] = useState<string[]>(initialTags);
  const [minRating, setMinRating] = useState<number | null>(initialMinRating);

  const { tagGroups, isLoading } = useTagGroups({ exclude: FILTER_EXCLUDED_TAG_GROUPS });

  const handleToggleTag = (tag: string) => {
    setSelectedTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    );
  };

  const handleToggleRating = (value: number) => {
    setMinRating((prev) => (prev === value ? null : value));
  };

  const handleApply = () => {
    sessionStorage.setItem("filterSelection", JSON.stringify(selectedTags));
    if (minRating !== null) {
      sessionStorage.setItem("ratingSelection", String(minRating));
    } else {
      sessionStorage.setItem("ratingSelection", "");
    }
    sessionStorage.removeItem("originalSelectedTags");
    sessionStorage.removeItem("originalMinRating");
    const savedSearch = sessionStorage.getItem("currentSearchTerm");
    if (savedSearch) {
      sessionStorage.setItem("searchTerm", savedSearch);
      sessionStorage.removeItem("currentSearchTerm");
    }
    setLocation(ROUTES.RECIPES_BASE());
  };

  const handleCancel = () => {
    const originalTags = JSON.parse(
      sessionStorage.getItem("originalSelectedTags") || "[]"
    );
    sessionStorage.setItem("filterSelection", JSON.stringify(originalTags));

    const originalRatingRaw = sessionStorage.getItem("originalMinRating");
    const originalRating =
      originalRatingRaw !== null && originalRatingRaw !== ""
        ? Number(originalRatingRaw)
        : null;
    if (originalRating !== null) {
      sessionStorage.setItem("ratingSelection", String(originalRating));
    } else {
      sessionStorage.setItem("ratingSelection", "");
    }

    sessionStorage.removeItem("originalSelectedTags");
    sessionStorage.removeItem("originalMinRating");
    const savedSearch = sessionStorage.getItem("currentSearchTerm");
    if (savedSearch) {
      sessionStorage.setItem("searchTerm", savedSearch);
      sessionStorage.removeItem("currentSearchTerm");
    }
    setLocation(ROUTES.RECIPES_BASE());
  };

  const handleClearAll = () => {
    setSelectedTags([]);
    setMinRating(null);
  };

  const totalActiveCount = selectedTags.length + (minRating !== null ? 1 : 0);

  return (
    <Box
      minH="100vh"
      bg="bg.canvas"
      display="flex"
      flexDirection="column"
      data-testid="filters-page"
    >
      {/* Header */}
      <Box
        bg="bg.surface"
        borderBottom="1px solid"
        borderColor="border.muted"
        position="sticky"
        top={0}
        zIndex={10}
        flexShrink={0}
      >
        <Box h="env(safe-area-inset-top, 0px)" bg="accent.emphasis" />
        <Flex px={4} py={3} align="center" justify="space-between">
          <HStack gap={3}>
            <IconButton
              aria-label="Go back"
              variant="ghost"
              size="sm"
              onClick={handleCancel}
              borderRadius="8px"
              bg="bg.muted"
              border="1px solid"
              borderColor="border.muted"
              _hover={{ bg: "border.muted" }}
            >
              <BackIcon />
            </IconButton>
            <Heading
              fontFamily="heading"
              fontWeight="800"
              fontSize="17px"
              color="text.primary"
            >
              Filters
            </Heading>
          </HStack>
          {totalActiveCount > 0 ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClearAll}
              fontFamily="body"
              fontWeight="500"
              fontSize="13px"
              color="text.muted"
              _hover={{ color: "text.primary", bg: "bg.muted" }}
            >
              Clear all
            </Button>
          ) : (
            <Box w="16px" />
          )}
        </Flex>
      </Box>

      {/* Body */}
      <Box flex={1} overflowY="auto" pb="96px">
        {/* Rating section — always shown above tag groups */}
        <Box>
          <Box px={4} pt={5} pb={4}>
            <Text
              fontFamily="heading"
              fontWeight="800"
              fontSize="10px"
              letterSpacing="0.14em"
              textTransform="uppercase"
              color="text.muted"
              mb={3}
            >
              Rating
            </Text>
            <Flex flexWrap="wrap" gap={2}>
              {RATING_CHIPS.map(({ label, value }) => {
                const isSelected = minRating === value;
                return (
                  <Box
                    key={value}
                    as="button"
                    onClick={() => handleToggleRating(value)}
                    px="12px"
                    py="6px"
                    borderRadius="8px"
                    border="1px solid"
                    fontFamily="body"
                    fontSize="13px"
                    fontWeight={isSelected ? "600" : "400"}
                    cursor="pointer"
                    transition="all 0.12s"
                    bg={isSelected ? "accent.emphasis" : "transparent"}
                    color={isSelected ? "white" : "text.primary"}
                    borderColor={isSelected ? "accent.emphasis" : "border.muted"}
                    _hover={{
                      bg: isSelected ? "accent.emphasisHover" : "bg.muted",
                      borderColor: isSelected ? "accent.emphasisHover" : "text.muted",
                    }}
                    data-testid={`rating-chip-${value}`}
                  >
                    <Flex as="span" align="center" gap="4px" display="inline-flex">
                      <Icon as={StarIcon} boxSize="13px" />
                      {label}
                    </Flex>
                  </Box>
                );
              })}
            </Flex>
          </Box>
          <Box h="1px" bg="border.muted" mx={4} />
        </Box>

        {isLoading ? (
          <VStack py={16} gap={3}>
            <Spinner size="md" color="accent.secondary" />
            <Text fontFamily="body" fontWeight="300" fontSize="13px" color="text.muted">
              Loading filters…
            </Text>
          </VStack>
        ) : (
          <VStack gap={0} align="stretch">
            {tagGroups.map((group, i) => (
              <Box key={group.name}>
                <Box px={4} pt={5} pb={4}>
                  {/* Group label */}
                  <Text
                    fontFamily="heading"
                    fontWeight="800"
                    fontSize="10px"
                    letterSpacing="0.14em"
                    textTransform="uppercase"
                    color="text.muted"
                    mb={3}
                  >
                    {group.name}
                  </Text>

                  {/* Chips */}
                  <Flex flexWrap="wrap" gap={2}>
                    {group.tags.map((tag) => {
                      const isSelected = selectedTags.includes(tag);
                      return (
                        <Box
                          key={tag}
                          as="button"
                          onClick={() => handleToggleTag(tag)}
                          px="12px"
                          py="6px"
                          borderRadius="8px"
                          border="1px solid"
                          fontFamily="body"
                          fontSize="13px"
                          fontWeight={isSelected ? "600" : "400"}
                          cursor="pointer"
                          transition="all 0.12s"
                          bg={isSelected ? "accent.emphasis" : "transparent"}
                          color={isSelected ? "white" : "text.primary"}
                          borderColor={isSelected ? "accent.emphasis" : "border.muted"}
                          _hover={{
                            bg: isSelected ? "accent.emphasisHover" : "bg.muted",
                            borderColor: isSelected ? "accent.emphasisHover" : "text.muted",
                          }}
                        >
                          {tag}
                        </Box>
                      );
                    })}
                  </Flex>
                </Box>
                {i < tagGroups.length - 1 && (
                  <Box h="1px" bg="border.muted" mx={4} />
                )}
              </Box>
            ))}
          </VStack>
        )}
      </Box>

      {/* Fixed Apply bar */}
      <Box
        position="fixed"
        bottom={0}
        left={{ base: 0, lg: '260px' }}
        right={0}
        bg="bg.surface"
        borderTop="1px solid"
        borderColor="border.muted"
        px={4}
        py={3}
        pb="calc(12px + env(safe-area-inset-bottom))"
        zIndex={10}
      >
        <Button
          w="100%"
          h="48px"
          bg="accent.emphasis"
          color="white"
          fontFamily="heading"
          fontWeight="800"
          fontSize="14px"
          borderRadius="13px"
          onClick={handleApply}
          boxShadow="0 4px 14px rgba(200,90,30,0.30)"
          _hover={{ bg: "accent.emphasisHover" }}
          _active={{ bg: "accent.emphasisHover" }}
        >
          Apply
          {totalActiveCount > 0 && (
            <Box
              as="span"
              ml={2}
              bg="rgba(255,255,255,0.25)"
              borderRadius="6px"
              px={2}
              py="1px"
              fontSize="12px"
              fontWeight="600"
            >
              {totalActiveCount}
            </Box>
          )}
        </Button>
      </Box>
    </Box>
  );
}
